import { defineMessages } from 'react-intl';

/**
 * The copy of the old Plan tab (designs m3, m3b, m3c, m4). The native apps still draw that Plan
 * tab and render these ids (`scripts/music24-native/i18n-keys.txt`), and the web Calendar names a
 * rollout day with them (`rolloutDayTitle`). Keep each id, text and placeholder as it is: the
 * native strings read them. The Releases v2 Plan tab uses `planMessages.ts`.
 */
export const legacyPlanMessages = defineMessages({
  postOn: { id: 'app.sml.releases.plan.postOn', defaultMessage: 'Post on' },
  connectChannel: { id: 'app.sml.releases.plan.connectChannel', defaultMessage: 'Connect a channel' },
  noChannel: {
    id: 'app.sml.releases.plan.noChannel',
    defaultMessage: 'Connect a channel to put the drafts on your calendar.',
  },
  count: {
    id: 'app.sml.releases.plan.count',
    defaultMessage:
      '{posts, plural, one {# draft} other {# drafts}} · {channels, plural, one {# channel} other {# channels}}',
  },
  add: {
    id: 'app.sml.releases.plan.add',
    defaultMessage:
      '{count, plural, =0 {Add drafts to the calendar} one {Add # draft to the calendar} other {Add # drafts to the calendar}}',
  },
  pastNote: {
    id: 'app.sml.releases.plan.pastNote',
    defaultMessage:
      '{count, plural, one {One day is} other {# days are}} in the past. MUSIC24 moves them to today, at the best time of each channel. You can change every date in the calendar.',
  },
  movedToToday: { id: 'app.sml.releases.plan.movedToToday', defaultMessage: 'Moved to today' },
  alreadyAdded: { id: 'app.sml.releases.plan.alreadyAdded', defaultMessage: 'Already on your calendar' },
  makeIt: { id: 'app.sml.releases.plan.makeIt', defaultMessage: 'Make it' },
  busy: { id: 'app.sml.releases.plan.busy', defaultMessage: 'This is already being made.' },
  makeFailed: { id: 'app.sml.releases.plan.makeFailed', defaultMessage: 'We could not start this asset. Try again.' },
  addFailed: { id: 'app.sml.releases.plan.addFailed', defaultMessage: 'We could not add the drafts. Try again.' },
  notMade: { id: 'app.sml.releases.plan.notMade', defaultMessage: '{what} · not made yet' },
  notMadeCredits: {
    id: 'app.sml.releases.plan.notMadeCredits',
    defaultMessage: '{what} · not made yet · {credits, plural, one {# credit} other {# credits}}',
  },
  working: { id: 'app.sml.releases.plan.working', defaultMessage: '{what} · being made' },
  dayToday: { id: 'app.sml.releases.plan.day.today', defaultMessage: 'Today' },
  dayRelease: { id: 'app.sml.releases.plan.day.release', defaultMessage: 'Release · {date}' },
  dayOffset: { id: 'app.sml.releases.plan.day.offset', defaultMessage: '{offset} d · {date}' },
  dayOffsetToday: { id: 'app.sml.releases.plan.day.offsetToday', defaultMessage: '{offset} d · today' },
  // Day titles (design m3, m4).
  titleAnnounce: { id: 'app.sml.releases.plan.title.announce', defaultMessage: 'Announce' },
  titleTeaser: { id: 'app.sml.releases.plan.title.teaser', defaultMessage: 'Teaser reel' },
  titleCountdown: { id: 'app.sml.releases.plan.title.countdown', defaultMessage: 'Countdown {days}' },
  titleOutNow: { id: 'app.sml.releases.plan.title.outNow', defaultMessage: 'Out now' },
  titleCanvas: { id: 'app.sml.releases.plan.title.canvas', defaultMessage: 'Canvas goes live' },
  titleSecondReel: { id: 'app.sml.releases.plan.title.secondReel', defaultMessage: 'Second reel' },
  titleOutNowPost: { id: 'app.sml.releases.plan.title.outNowPost', defaultMessage: 'Out now post' },
  titleAiReel: { id: 'app.sml.releases.plan.title.aiReel', defaultMessage: 'AI reel' },
  titleStory: { id: 'app.sml.releases.plan.title.story', defaultMessage: 'Story' },
  titleArtworkInMotion: { id: 'app.sml.releases.plan.title.artworkInMotion', defaultMessage: 'Artwork in motion' },
  // What the day posts.
  whatPreSaveStory: { id: 'app.sml.releases.plan.what.preSaveStory', defaultMessage: 'Pre-save story' },
  whatOutNowStory: { id: 'app.sml.releases.plan.what.outNowStory', defaultMessage: 'Out now story' },
  whatStory: { id: 'app.sml.releases.plan.what.story', defaultMessage: 'Story' },
  whatOutNowPost: { id: 'app.sml.releases.plan.what.outNowPost', defaultMessage: 'Out now post' },
  whatAiReel: { id: 'app.sml.releases.plan.what.aiReel', defaultMessage: 'AI reel' },
  whatArtworkInMotion: { id: 'app.sml.releases.plan.what.artworkInMotion', defaultMessage: 'Artwork in motion' },
  whatCanvasTask: {
    id: 'app.sml.releases.plan.what.canvasTask',
    defaultMessage: 'Add it in Spotify for Artists, then mark it in the plan. A task, not a post.',
  },
  // m3b
  doneTitle: {
    id: 'app.sml.releases.plan.done.title',
    defaultMessage:
      '{count, plural, one {# draft is on your calendar. Approve it there.} other {# drafts are on your calendar. Approve them there.}}',
  },
  doneNothingNew: {
    id: 'app.sml.releases.plan.done.nothingNew',
    defaultMessage: 'These drafts are already on your calendar.',
  },
  doneMissing: {
    id: 'app.sml.releases.plan.done.missing',
    defaultMessage: 'Not added: {days} (not made yet). Make them, then add the drafts again.',
  },
  openCalendar: { id: 'app.sml.releases.plan.openCalendar', defaultMessage: 'Open the calendar' },
  // m3c
  noDateTitle: {
    id: 'app.sml.releases.plan.noDate.title',
    defaultMessage: 'Add the release date in the catalog first.',
  },
  noDateBody: {
    id: 'app.sml.releases.plan.noDate.body',
    defaultMessage: 'The rollout counts its days from the release date.',
  },
  noDateLink: { id: 'app.sml.releases.plan.noDate.link', defaultMessage: 'Open {title} in the catalog' },
  noDatePush: {
    id: 'app.sml.releases.plan.noDate.push',
    defaultMessage: 'Or push the song now with a spike or an evergreen plan below.',
  },
  // m4
  pushTitle: { id: 'app.sml.releases.plan.push.title', defaultMessage: 'Push {title}' },
  pushOutSince: {
    id: 'app.sml.releases.plan.push.outSince',
    defaultMessage: 'Out since {date}. Pick how you want to push it. MUSIC24 plans the posts from today.',
  },
  pushIntro: {
    id: 'app.sml.releases.plan.push.intro',
    defaultMessage: 'Pick how you want to push it. MUSIC24 plans the posts from today.',
  },
  spikeTitle: { id: 'app.sml.releases.plan.push.spike.title', defaultMessage: 'A spike (7 days)' },
  spikeBody: {
    id: 'app.sml.releases.plan.push.spike.body',
    defaultMessage: 'Four posts in one week: an out now post, an AI reel, a story and the artwork in motion.',
  },
  spikeDays: { id: 'app.sml.releases.plan.push.spike.days', defaultMessage: 'Today · +2 d · +4 d · +6 d' },
  evergreenTitle: { id: 'app.sml.releases.plan.push.evergreen.title', defaultMessage: 'Evergreen (4 weeks)' },
  evergreenBody: {
    id: 'app.sml.releases.plan.push.evergreen.body',
    defaultMessage: 'One post a week for a month. Keeps the song in front of your fans.',
  },
  evergreenDays: { id: 'app.sml.releases.plan.push.evergreen.days', defaultMessage: 'Today · +7 d · +14 d · +21 d' },
});
